import axios from 'axios';

// Cache to prevent duplicate requests
const requestCache = {
  lastRequest: null,
  inProgress: false,
  timestamp: 0,
  debounceTime: 2000 // 2 seconds
};

/**
 * Fetch all lender settings
 * 
 * @returns {Promise<Array>} Array of lender settings
 */
export const getLenderSettings = async () => {
  try {
    console.log('🔄 API: Calling GET /api/lender-settings...');
    const response = await axios.get('/api/lender-settings');
    console.log('📡 API: GET /api/lender-settings response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ API: Error fetching lender settings:', error);
    throw error;
  }
};

/**
 * Save lender settings
 * 
 * @param {Array} settings - Array of lender settings objects
 * @returns {Promise<Array>} Updated array of lender settings with unique IDs
 */
export const saveLenderSettings = async (settings) => {
  try {
    // Check if we have a request in progress or if the last request was too recent
    const now = Date.now();
    if (requestCache.inProgress || (now - requestCache.timestamp < requestCache.debounceTime && 
        JSON.stringify(settings) === requestCache.lastRequest)) {
      console.log('Preventing duplicate save request');
      return []; // Return empty array to prevent state updates
    }
    
    // Mark request as in progress
    requestCache.inProgress = true;
    requestCache.lastRequest = JSON.stringify(settings);
    requestCache.timestamp = now;
    
    const response = await axios.post('/api/lender-settings', settings);
    
    // Reset in progress flag
    requestCache.inProgress = false;
    
    return response.data;
  } catch (error) {
    // Reset in progress flag on error
    requestCache.inProgress = false;
    
    console.error('Error saving lender settings:', error);
    throw error;
  }
}; 

/**
 * Get watermark setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<boolean>} Whether watermarking is enabled for this lender
 */
export const getLenderWatermarkSetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lender-settings/lender/${lenderId}/watermark`);
    return response.data.watermarkEnabled;
  } catch (error) {
    console.error(`Error fetching watermark setting for lender ${lenderId}:`, error);
    return false; // Default to false on error
  }
};

/**
 * Get contact info scramble setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<boolean>} Whether contact info scrambling is enabled for this lender
 */
export const getLenderScrambleSetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lender-settings/lender/${lenderId}/scramble`);
    return response.data.contactInfoScramble;
  } catch (error) {
    console.error(`Error fetching contact info scramble setting for lender ${lenderId}:`, error);
    return false; // Default to false on error
  }
}; 

/**
 * Get active setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<boolean>} Whether the lender is active
 */
export const getLenderActiveSetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lender-settings/lender/${lenderId}/active`);
    return response.data.active;
  } catch (error) {
    console.error(`Error fetching active setting for lender ${lenderId}:`, error);
    return true; // Default to active on error
  }
}; 

/**
 * Get emailOnly setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<boolean>} - Whether the lender is email only
 */
export const getLenderEmailOnlySetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lender-settings/lender/${lenderId}/emailOnly`);
    return response.data.emailOnly;
  } catch (error) {
    console.error(`Error fetching emailOnly setting for lender ${lenderId}:`, error);
    return false; // Default to false on error
  }
}; 

/**
 * Get tier setting for a specific lender
 * 
 * @param {string} lenderId - The ID of the lender
 * @returns {Promise<number>} - The tier number (1, 2, or 3)
 */
export const getLenderTierSetting = async (lenderId) => {
  try {
    const response = await axios.get(`/api/lender-settings/lender/${lenderId}/tier`);
    return response.data.tier;
  } catch (error) {
    console.error(`Error fetching tier setting for lender ${lenderId}:`, error);
    return 1; // Default to tier 1 on error
  }
}; 

/**
 * Get all active lenders with their settings for submission component
 * Filters out inactive lenders and returns only those that are active
 * 
 * @param {string} financingType - The financing type ('working-capital' or 'equipment-financing')
 * @returns {Promise<Array>} Array of active lender objects with tier and emailOnly info
 */
export const getActiveLendersForSubmission = async (financingType = 'working-capital') => {
  try {
    console.log(`🔄 API: getActiveLendersForSubmission called with financingType: ${financingType}`);
    
    const allSettings = await getLenderSettings();
    console.log(`📊 API: getLenderSettings returned ${allSettings.length} total settings:`, allSettings);
    
    // Filter by financing type and active status
    const filteredByType = allSettings.filter(lender => lender.financingType === financingType);
    console.log(`🔍 API: Found ${filteredByType.length} lenders with financingType '${financingType}':`, filteredByType);
    
    const filteredByActive = filteredByType.filter(lender => lender.active === true);
    console.log(`✅ API: Found ${filteredByActive.length} active lenders:`, filteredByActive);
    
    const activeLenders = filteredByActive
      .map(lender => ({
        id: lender.stringId,
        name: lender.lenderName,
        tier: lender.tier || 1,
        emailOnly: lender.emailOnly || false,
        watermarkEnabled: lender.watermarkEnabled || false,
        contactInfoScramble: lender.contactInfoScramble || false,
        uniqueLenderId: lender.uniqueLenderId,
        crmAccountId: lender.crmAccountId
      }))
      .sort((a, b) => {
        // Sort by tier first, then by name
        if (a.tier !== b.tier) {
          return a.tier - b.tier;
        }
        return a.name.localeCompare(b.name);
      });
    
    console.log(`🎯 API: Final processed active ${financingType} lenders (${activeLenders.length}):`, activeLenders);
    return activeLenders;
  } catch (error) {
    console.error(`❌ API: Error fetching active lenders for ${financingType}:`, error);
    return []; // Return empty array on error
  }
};

/**
 * Seed the database with hardcoded lender data
 * Useful for initial setup or migrating from hardcoded arrays
 * 
 * @param {Array} lenders - Array of lender objects to seed
 * @returns {Promise<Object>} Result of seeding operation
 */
export const seedLenderDatabase = async (lenders) => {
  try {
    const response = await axios.post('/api/lender-settings/seed', { lenders });
    return response.data;
  } catch (error) {
    console.error('Error seeding lender database:', error);
    throw error;
  }
};

// Legacy aliases for backward compatibility
export const getSecuritySettings = getLenderSettings;
export const saveSecuritySettings = saveLenderSettings;

/**
 * Create a new custom email-only lender
 * 
 * @param {Object} lenderData - Lender data object with stringId, lenderName, emailToAddress, etc.
 * @returns {Promise<Object>} Created lender object
 */
export const createEmailOnlyLender = async (lenderData) => {
  try {
    console.log('🔄 API: Creating email-only lender:', lenderData);
    const response = await axios.post('/api/lender-settings/email-lender', lenderData);
    console.log('✅ API: Email-only lender created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ API: Error creating email-only lender:', error);
    throw error;
  }
};

/**
 * Update email configuration for a lender
 * 
 * @param {string} lenderId - The ID of the lender to update
 * @param {Object} lenderData - Updated lender data
 * @returns {Promise<Object>} Updated lender object
 */
export const updateEmailLender = async (lenderId, lenderData) => {
  try {
    console.log(`🔄 API: Updating email lender ${lenderId}:`, lenderData);
    const response = await axios.put(`/api/lender-settings/email-lender/${lenderId}`, lenderData);
    console.log('✅ API: Email lender updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ API: Error updating email lender ${lenderId}:`, error);
    throw error;
  }
};

/**
 * Delete a lender (any type)
 * 
 * @param {string} lenderId - The ID of the lender to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteLender = async (lenderId) => {
  try {
    console.log(`🔄 API: Deleting lender ${lenderId}`);
    const response = await axios.delete(`/api/lender-settings/lender/${lenderId}`);
    console.log('✅ API: Lender deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ API: Error deleting lender ${lenderId}:`, error);
    throw error;
  }
};

/**
 * Delete a custom email-only lender
 * 
 * @param {string} lenderId - The ID of the lender to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteEmailLender = async (lenderId) => {
  try {
    console.log(`🔄 API: Deleting email lender ${lenderId}`);
    const response = await axios.delete(`/api/lender-settings/email-lender/${lenderId}`);
    console.log('✅ API: Email lender deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ API: Error deleting email lender ${lenderId}:`, error);
    throw error;
  }
};

/**
 * Get all email-only lenders
 * 
 * @returns {Promise<Array>} Array of email-only lenders
 */
export const getEmailOnlyLenders = async () => {
  try {
    console.log('🔄 API: Fetching email-only lenders');
    const response = await axios.get('/api/lender-settings/email-lenders');
    console.log('📡 API: Email-only lenders fetched:', response.data);
    return response.data.lenders || [];
  } catch (error) {
    console.error('❌ API: Error fetching email-only lenders:', error);
    throw error;
  }
}; 